/** Queries and mutations for settings, staff, roles and the subscription. */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, unwrap, type Schemas } from '../../api/client';
import { settingsQueryKey } from '../../shop/ShopProvider';

export type ShopSettings = Schemas['ShopSettings'];
export type SettingsPatch = Omit<Schemas['UpdateShopSettingsRequest'], 'version'>;
export type TaxRate = Schemas['TaxRate'];
export type Numbering = Schemas['DocumentNumbering'];
export type Staff = Schemas['Staff'];
export type Invitation = Schemas['StaffInvitation'];
export type InvitationSend = Schemas['StaffInvitationSendResult'];
export type Role = Schemas['Role'];

export const keys = {
  settings: settingsQueryKey,
  numbering: ['settings', 'numbering'] as const,
  taxRates: ['tax-rates'] as const,
  staff: ['staff'] as const,
  invitations: ['staff', 'invitations'] as const,
  roles: ['roles'] as const,
  permissions: ['permissions'] as const,
  subscription: ['subscription'] as const,
  /** Under `subscription`, so invalidating the subscription refreshes it too. */
  subscriptionStatus: ['subscription', 'status'] as const,
  supportContact: ['subscription', 'support-contact'] as const,
};

export function useSettings() {
  return useQuery({
    queryKey: keys.settings,
    queryFn: () => unwrap(api.GET('/settings')),
    staleTime: 5 * 60_000,
  });
}

/**
 * Saves part of the settings with the current `version`. The app re-themes from the `['settings']`
 * query, so the result is written straight into the cache and the query is invalidated.
 */
export function useSaveSettings() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (patch: SettingsPatch) => {
      const current = qc.getQueryData<ShopSettings>(keys.settings);
      const version = current?.version ?? (await unwrap(api.GET('/settings'))).version;
      return unwrap(api.PATCH('/settings', { body: { ...patch, version } }));
    },
    onSuccess: (data) => {
      qc.setQueryData(keys.settings, data);
      void qc.invalidateQueries({ queryKey: keys.settings, exact: true });
    },
  });
}

export function useNumbering() {
  return useQuery({
    queryKey: keys.numbering,
    queryFn: () => unwrap(api.GET('/settings/numbering')),
  });
}

export function useTaxRates() {
  return useQuery({ queryKey: keys.taxRates, queryFn: () => unwrap(api.GET('/tax-rates')) });
}

export function useRoles(enabled = true) {
  return useQuery({ queryKey: keys.roles, queryFn: () => unwrap(api.GET('/roles')), enabled });
}

export function usePermissionCatalog(enabled = true) {
  return useQuery({
    queryKey: keys.permissions,
    queryFn: () => unwrap(api.GET('/permissions')),
    staleTime: 60 * 60_000,
    enabled,
  });
}

export function useSubscription(enabled = true) {
  return useQuery({
    queryKey: keys.subscription,
    queryFn: () => unwrap(api.GET('/subscription')),
    enabled,
  });
}

/** Dates and the trial flag for any signed-in user: the banner and the locked screen. */
export function useSubscriptionNotice(enabled = true) {
  return useQuery({
    queryKey: keys.subscriptionStatus,
    queryFn: () => unwrap(api.GET('/subscription/status')),
    enabled,
    refetchInterval: 10 * 60_000,
  });
}

export function useSupportContact(enabled = true) {
  return useQuery({
    queryKey: keys.supportContact,
    queryFn: () => unwrap(api.GET('/subscription/support-contact')),
    enabled,
    staleTime: 30 * 60_000,
  });
}

/** Header params for endpoints whose schema requires an explicit Idempotency-Key. */
export function idempotency() {
  return { header: { 'idempotency-key': crypto.randomUUID() } };
}
